/**
 * Página «País e moeda» (#486 item 1, decisão do dono): o dono do restaurante
 * muda sozinho o país (TODOS os códigos do mapa país → moeda do shared — 249
 * países ISO 3166 + XK) e a moeda (ISO 4217 suportada). A moeda acompanha o
 * país por omissão e continua editável.
 *
 * Regra de ouro (#171/#486): mudar país/moeda NÃO converte preços — os itens
 * guardados mantêm os valores; os novos nascem na moeda nova. O aviso fica
 * fixo na página e volta na confirmação. Decisão do dono: página escondida
 * durante a impersonação (a equipe da Pratzu não muda país/moeda).
 */
import { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ALL_COUNTRY_CODES,
  SUPPORTED_CURRENCIES,
  currencyForCountry,
} from '@self-restaurant/shared'
import { config } from '../lib/api.js'
import { countryName, countryOptions } from '../lib/countries.js'
import { currencyName } from '../lib/currency-names.js'
import { jwtHasImpersonatingClaim } from '../lib/impersonation.js'
import { useI18n } from '../lib/i18n.js'
import { Button, Card, Modal, Select, Spinner, toast } from '../components/ui/index.js'

/** Moedas do painel: todas as suportadas, «nome (ISO)» na língua ativa. */
export function currencyOptions(lang: string): { value: string; label: string }[] {
  let collator: Intl.Collator
  try {
    collator = new Intl.Collator(lang)
  } catch {
    collator = new Intl.Collator()
  }
  return SUPPORTED_CURRENCIES.map((code) => ({ value: code, label: currencyName(code, lang) })).sort(
    (a, b) => collator.compare(a.label, b.label),
  )
}

export function CountryCurrency() {
  const { t, lang } = useI18n()
  const qc = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { data: cfg, isLoading } = useQuery({ queryKey: ['config'], queryFn: config.get })

  const country = cfg?.country_code ?? ''
  const currency = cfg?.currency ?? ''
  // rascunho separado do valor guardado: o botão só liga com mudança real
  const [countryDraft, setCountryDraft] = useState<string | null>(null)
  const [currencyDraft, setCurrencyDraft] = useState<string | null>(null)
  const draftCountry = countryDraft ?? country
  const draftCurrency = currencyDraft ?? currency
  const dirty = draftCountry !== country || draftCurrency !== currency

  const countryOpts = useMemo(
    () => countryOptions([ALL_COUNTRY_CODES], lang, [country || null]),
    [lang, country],
  )
  const currencyOpts = useMemo(() => currencyOptions(lang), [lang])

  function pickCountry(next: string) {
    setCountryDraft(next)
    // moeda derivada do país por omissão — o dono ainda pode trocar
    const derived = currencyForCountry(next)
    if (derived) setCurrencyDraft(derived)
  }

  const mut = useMutation({
    mutationFn: () => config.update({ country_code: draftCountry, currency: draftCurrency }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      setCountryDraft(null)
      setCurrencyDraft(null)
      setConfirmOpen(false)
      toast.success(t('admin.country_currency.saved'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // #486 item 1: decisão do dono — durante a impersonação a página não abre
  // (o item do menu também some; URL direta volta ao início). Depois dos
  // hooks: a ordem de chamada é a mesma em todo render.
  if (jwtHasImpersonatingClaim()) return <Navigate to="/" replace />

  if (isLoading || !cfg) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="country-currency-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.country_currency.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('admin.country_currency.subtitle')}</p>
      </div>

      {/* aviso fixo: a troca NUNCA converte preços (#171/#486, decisão PO) */}
      <div
        role="note"
        data-testid="country-currency-warning"
        className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900"
      >
        {t('admin.country_currency.no_conversion')}
      </div>

      <Card>
        <div className="space-y-4">
          <Select
            label={t('admin.country_currency.country')}
            value={draftCountry}
            onChange={(e) => pickCountry(e.target.value)}
            options={countryOpts}
            data-testid="country-currency-select"
          />
          <Select
            label={t('admin.country_currency.currency')}
            value={draftCurrency}
            onChange={(e) => setCurrencyDraft(e.target.value)}
            options={currencyOpts}
            data-testid="currency-select"
          />
          <p className="text-sm text-gray-500">{t('admin.country_currency.currency_hint')}</p>
          <Button
            disabled={!dirty}
            loading={mut.isPending}
            onClick={() => setConfirmOpen(true)}
            data-testid="country-currency-save"
          >
            {t('admin.common.save')}
          </Button>
        </div>
      </Card>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('admin.country_currency.confirm_title')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              loading={mut.isPending}
              onClick={() => mut.mutate()}
              data-testid="country-currency-confirm"
            >
              {t('admin.common.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-gray-700" data-testid="country-currency-modal">
          {draftCountry !== country && (
            <p data-testid="confirm-country">
              {t('admin.country_currency.confirm_country', {
                from: countryName(country, lang),
                to: countryName(draftCountry, lang),
              })}
            </p>
          )}
          {draftCurrency !== currency && (
            <p data-testid="confirm-currency">
              {t('admin.country_currency.confirm_currency', {
                from: currencyName(currency, lang),
                to: currencyName(draftCurrency, lang),
              })}
            </p>
          )}
          <p className="rounded-lg bg-yellow-50 border border-yellow-300 px-3 py-2 text-yellow-900">
            {t('admin.country_currency.confirm_no_conversion')}
          </p>
        </div>
      </Modal>
    </div>
  )
}
